import { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { User, Lock, CheckCircle, AlertCircle, Shield } from 'lucide-react';
import { Button } from '../ui/Button';
import { PageHeader } from '../ui/PageHeader';
import { toast } from '../ui/Toaster';

export function Profile() {
  const { profile } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    if (!currentPassword) {
      setMessage({ type: 'error', text: 'Ingresa tu contraseña actual para confirmar el cambio.' });
      return;
    }
    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'Las contraseñas nuevas no coinciden.' });
      return;
    }
    if (newPassword.length < 6) {
      setMessage({ type: 'error', text: 'La nueva contraseña debe tener al menos 6 caracteres.' });
      return;
    }
    if (newPassword === currentPassword) {
      setMessage({ type: 'error', text: 'La nueva contraseña debe ser distinta a la actual.' });
      return;
    }
    if (!profile?.email) {
      setMessage({ type: 'error', text: 'No pudimos verificar tu sesión. Vuelve a iniciar sesión.' });
      return;
    }

    setLoading(true);
    try {
      const { error: reauthError } = await supabase.auth.signInWithPassword({
        email: profile.email,
        password: currentPassword,
      });
      if (reauthError) {
        setMessage({ type: 'error', text: 'La contraseña actual es incorrecta.' });
        return;
      }

      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;

      setMessage({ type: 'success', text: 'Contraseña actualizada correctamente.' });
      toast.success('Contraseña actualizada');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      console.error('Error changing password:', error);
      setMessage({ type: 'error', text: error.message || 'No se pudo cambiar la contraseña.' });
    } finally {
      setLoading(false);
    }
  };

  const roleLabel =
    profile?.role === 'admin' ? 'Administrador' : profile?.role === 'docente' ? 'Docente' : 'Alumno';
  const roleChip =
    profile?.role === 'admin'
      ? 'chip-lime'
      : profile?.role === 'docente'
      ? 'chip-cyan'
      : 'chip-emerald';

  return (
    <div className="space-y-6 max-w-5xl p-4 sm:p-6 lg:p-8 overflow-y-auto">
      <PageHeader
        eyebrow="Mi cuenta"
        eyebrowIcon={User}
        title="Configuración de perfil"
        description="Administra tu información personal y tu seguridad."
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section aria-labelledby="info-heading" className="glass-surface rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-11 h-11 rounded-xl bg-cubus-lime/15 border border-cubus-lime/30 flex items-center justify-center">
              <User className="w-5 h-5 text-cubus-lime" aria-hidden="true" />
            </div>
            <div>
              <h2 id="info-heading" className="text-lg font-semibold text-white text-display">
                Información personal
              </h2>
              <p className="text-sm text-muted">Datos de tu cuenta</p>
            </div>
          </div>

          <dl className="space-y-4">
            <div>
              <dt className="block text-xs font-medium text-subtle mb-1 uppercase tracking-wide">
                Nombre completo
              </dt>
              <dd className="px-4 py-3 bg-white/[0.03] border border-white/[0.08] rounded-lg text-white">
                {profile?.full_name || '—'}
              </dd>
            </div>
            <div>
              <dt className="block text-xs font-medium text-subtle mb-1 uppercase tracking-wide">Email</dt>
              <dd className="px-4 py-3 bg-white/[0.03] border border-white/[0.08] rounded-lg text-white break-all">
                {profile?.email || '—'}
              </dd>
            </div>
            <div>
              <dt className="block text-xs font-medium text-subtle mb-1 uppercase tracking-wide">Rol</dt>
              <dd className="px-4 py-3 bg-white/[0.03] border border-white/[0.08] rounded-lg">
                <span className={`chip ${roleChip}`}>{roleLabel}</span>
              </dd>
            </div>
          </dl>
        </section>

        <section aria-labelledby="password-heading" className="glass-surface rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-11 h-11 rounded-xl bg-cubus-lime/15 border border-cubus-lime/30 flex items-center justify-center">
              <Lock className="w-5 h-5 text-cubus-lime" aria-hidden="true" />
            </div>
            <div>
              <h2 id="password-heading" className="text-lg font-semibold text-white text-display">
                Cambiar contraseña
              </h2>
              <p className="text-sm text-muted">Se te pedirá confirmar tu contraseña actual.</p>
            </div>
          </div>

          <form onSubmit={handleChangePassword} className="space-y-4">
            {message && (
              <div
                role="alert"
                className={`p-3 rounded-lg flex items-start gap-2 text-sm ${
                  message.type === 'success'
                    ? 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/30'
                    : 'bg-red-500/10 text-red-300 border border-red-500/30'
                }`}
              >
                {message.type === 'success' ? (
                  <CheckCircle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
                ) : (
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
                )}
                <span>{message.text}</span>
              </div>
            )}

            <div>
              <label htmlFor="current-password" className="block text-xs font-medium text-subtle mb-1.5 uppercase tracking-wide">
                Contraseña actual
              </label>
              <input
                id="current-password"
                type="password"
                required
                autoComplete="current-password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Tu contraseña actual"
                className="w-full px-4 py-2.5 glass-input rounded-lg text-white placeholder:text-faint"
              />
            </div>

            <div>
              <label htmlFor="new-password" className="block text-xs font-medium text-subtle mb-1.5 uppercase tracking-wide">
                Nueva contraseña
              </label>
              <input
                id="new-password"
                type="password"
                required
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Mínimo 6 caracteres"
                className="w-full px-4 py-2.5 glass-input rounded-lg text-white placeholder:text-faint"
                minLength={6}
              />
            </div>

            <div>
              <label htmlFor="confirm-password" className="block text-xs font-medium text-subtle mb-1.5 uppercase tracking-wide">
                Confirmar nueva contraseña
              </label>
              <input
                id="confirm-password"
                type="password"
                required
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repite la nueva contraseña"
                className="w-full px-4 py-2.5 glass-input rounded-lg text-white placeholder:text-faint"
                minLength={6}
              />
            </div>

            <Button type="submit" variant="lime" fullWidth loading={loading}>
              Cambiar contraseña
            </Button>
          </form>
        </section>
      </div>

      <section className="glass-surface rounded-2xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 rounded-lg bg-cubus-cyan/10 border border-cubus-cyan/20 flex items-center justify-center">
            <Shield className="w-4 h-4 text-cubus-cyan" aria-hidden="true" />
          </div>
          <h3 className="text-base font-semibold text-white text-display">Recomendaciones de seguridad</h3>
        </div>
        <ul className="space-y-2 text-sm text-muted">
          {[
            'Tu contraseña debe tener al menos 6 caracteres.',
            'Combina letras, números y símbolos para mayor seguridad.',
            'No compartas tu contraseña con nadie.',
            'Si olvidaste tu contraseña, contacta al administrador para resetearla.',
          ].map((tip) => (
            <li key={tip} className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
              <span>{tip}</span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
